import type { DurationValue } from "components/ds/DurationField";
import type { MFRequestableRole } from "state/members/access_v2/catalogs";
import {
  fitToRole,
  objectOf,
  roleSelectionProblem,
  type RequestFor,
} from "state/members/access_v2/roleRequests";
import { checkDurationAgainstMax, type DurationTerms } from "./durationLimits";
import { EVERY_RECIPIENT, resourceKey, type FlatRequestItem } from "./types";

// Selectable resource id → the role ids ticked on it. Keyed on the resource
// like the basket, so moving a resource to its card in another catalog keeps
// the roles chosen for it.
export type RoleSelection = Record<string, string[]>;

// Recipient id → the roles ticked for them. Ticks made on a picker every
// recipient shares land on EVERY_RECIPIENT, which also covers anyone added to
// the request afterwards until roles are ticked for them alone.
export type RecipientRoleSelection = Record<string, RoleSelection>;

export type RequestTerms = DurationTerms & { requiresComment: boolean };

export const requestableRoles = (
  item: FlatRequestItem,
): MFRequestableRole[] | null =>
  item.resource.selectableResource.entitlements ?? null;

export const selectedRoleIds = (
  item: FlatRequestItem,
  selection: RoleSelection,
): string[] => selection[resourceKey(item)] ?? [];

export const selectedRoles = (
  item: FlatRequestItem,
  selection: RoleSelection,
): MFRequestableRole[] => {
  const ids = selectedRoleIds(item, selection);
  return (requestableRoles(item) ?? []).filter((r) => ids.includes(r.id));
};

export const approvalTone = (
  item: FlatRequestItem,
  selection: RoleSelection,
): "approval" | "instant" => {
  const resource = item.resource.selectableResource;
  const needsApproval = resource.entitlements
    ? selectedRoles(item, selection).some((r) => r.requiresApproval)
    : resource.requiresApproval;
  return needsApproval ? "approval" : "instant";
};

// What a request for this item may ask for: the resource's own limits. On a
// resource whose access follows its roles each role takes its own duration,
// fitted to its own terms, so any duration chosen for the item is accepted and
// only the comment requirement is the ticked roles' together.
export const itemTerms = (
  item: FlatRequestItem,
  selection: RoleSelection,
): RequestTerms => {
  const resource = item.resource.selectableResource;
  if (!resource.entitlements) return resource;
  return {
    allowIndefinite: true,
    indefiniteOnly: false,
    maxTimeUnit: null,
    maxTimeValue: null,
    requiresComment: selectedRoles(item, selection).some(
      (r) => r.terms.requiresComment,
    ),
  };
};

// Why this role can't be requested for `duration` once fitted to its terms:
// only a role that allows no duration at all.
export const roleDurationProblem = (
  role: MFRequestableRole,
  duration: DurationValue,
): string | null => {
  const check = checkDurationAgainstMax(
    fitToRole(duration, role.terms),
    role.terms,
  );
  return check.exceeds ? `${role.name}: ${check.reason}` : null;
};

export const itemRoleProblem = (
  item: FlatRequestItem,
  selection: RoleSelection,
  requestFor: RequestFor = "you",
): string | null => {
  const roles = requestableRoles(item);
  const selected = selectedRoles(item, selection);
  if (roles?.length && !selected.length && selectedRoleIds(item, selection).length)
    return `None of the ticked roles can be requested for ${objectOf(requestFor)}.`;
  return roleSelectionProblem(roles, selected, requestFor);
};

export const recipientRoles = (
  selection: RecipientRoleSelection,
  recipientId: string,
): RoleSelection => ({
  ...selection[EVERY_RECIPIENT],
  ...selection[recipientId],
});

const toggled = (ids: string[], roleId: string) =>
  ids.includes(roleId) ? ids.filter((id) => id !== roleId) : [...ids, roleId];

const shareRoles = (
  selection: RecipientRoleSelection,
  resourceId: string,
  recipientIds: string[],
  ids: string[],
): RecipientRoleSelection => {
  const next = Object.fromEntries(
    Object.entries(selection).map(([recipientId, roles]) => {
      if (!recipientIds.includes(recipientId)) return [recipientId, roles];
      const { [resourceId]: _dropped, ...rest } = roles;
      return [recipientId, rest];
    }),
  );
  return {
    ...next,
    [EVERY_RECIPIENT]: { ...next[EVERY_RECIPIENT], [resourceId]: ids },
  };
};

// Toggle a role from what is ticked for `recipientId`, for them alone or, on
// the picker everyone in `sharedWith` shares, for all of them: a shared tick
// lands on EVERY_RECIPIENT and replaces what was ticked for any of them alone.
// Anyone else keeps their own ticks.
export const toggleRole = (
  selection: RecipientRoleSelection,
  recipientId: string,
  item: FlatRequestItem,
  roleId: string,
  sharedWith?: string[],
): RecipientRoleSelection => {
  const resourceId = resourceKey(item);
  const ids = toggled(
    selectedRoleIds(item, recipientRoles(selection, recipientId)),
    roleId,
  );
  if (!sharedWith)
    return {
      ...selection,
      [recipientId]: { ...selection[recipientId], [resourceId]: ids },
    };
  return shareRoles(selection, resourceId, sharedWith, ids);
};

// Once `recipientIds` have the same roles ticked on the item, hold them on
// EVERY_RECIPIENT so the picker they now share shows and edits them, and anyone
// added afterwards starts from them. Unchanged unless they all agree and one of
// them holds ticks of their own.
export const poolRoles = (
  selection: RecipientRoleSelection,
  item: FlatRequestItem,
  recipientIds: string[],
): RecipientRoleSelection => {
  const resourceId = resourceKey(item);
  const ticks = recipientIds.map((id) =>
    [...selectedRoleIds(item, recipientRoles(selection, id))].sort().join(),
  );
  if (
    new Set(ticks).size !== 1 ||
    !recipientIds.some((id) => selection[id]?.[resourceId] !== undefined)
  )
    return selection;
  return shareRoles(
    selection,
    resourceId,
    recipientIds,
    selectedRoleIds(item, recipientRoles(selection, recipientIds[0])),
  );
};

export const dropResourceRoles = (
  selection: RecipientRoleSelection,
  resourceId: string,
): RecipientRoleSelection =>
  Object.fromEntries(
    Object.entries(selection).map(([recipientId, roles]) => {
      const { [resourceId]: _dropped, ...rest } = roles;
      return [recipientId, rest];
    }),
  );
